/**
 * CrewVideoUploader ✦
 *
 * Drop-in widget for crew intro videos and supplier opportunity
 * clips. Mobile-first, neon glow progress ring while uploading.
 * The clip is sent raw — the server transcodes to 720×1280 H.264
 * + 1.5Mbps + faststart so the swipe feed never chokes on heavy
 * source files.
 *
 * Props:
 *   kind:       "opportunity" | "profile"
 *   maxSeconds: hard cap shown in the helper text
 *   value:      current { video_url, poster_url }
 *   onChange:   (next) => void  — called after a successful upload
 *               or when the clip is removed
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { VideoCamera, Trash, Lightning, CheckCircle } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PURPLE = '#6C3BFF';
const PINK = '#FF3CAC';
const BLUE = '#33C3FF';
const TEXT = '#FFFFFF';
const TEXT_MUTED = '#B8B8D1';
const PANEL_BORDER = 'rgba(255,255,255,0.1)';
const FONT_STACK = `'Inter', 'Poppins', system-ui, sans-serif`;

const ProgressRing = ({ pct }) => {
  const r = 30;
  const c = 2 * Math.PI * r;
  const off = c - (pct / 100) * c;
  return (
    <svg width="76" height="76" viewBox="0 0 76 76">
      <defs>
        <linearGradient id="crewUploadGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={PURPLE} />
          <stop offset="50%" stopColor={PINK} />
          <stop offset="100%" stopColor={BLUE} />
        </linearGradient>
      </defs>
      <circle cx="38" cy="38" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="6" fill="none" />
      <circle
        cx="38" cy="38" r={r}
        stroke="url(#crewUploadGrad)" strokeWidth="6" fill="none"
        strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
        transform="rotate(-90 38 38)"
        style={{ transition: 'stroke-dashoffset 0.18s ease-out', filter: `drop-shadow(0 0 6px ${PINK}aa)` }}
      />
      <text x="38" y="44" textAnchor="middle"
        style={{ fill: TEXT, fontFamily: FONT_STACK, fontSize: 14, fontWeight: 900 }}>
        {Math.round(pct)}%
      </text>
    </svg>
  );
};

const CrewVideoUploader = ({
  kind = 'profile',
  maxSeconds = 10,
  value = {},
  onChange,
  testId = 'crew-video-uploader',
}) => {
  const inputRef = useRef(null);
  const [pct, setPct] = useState(0);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(''); // 'upload' | 'optimise' | ''
  const [error, setError] = useState('');

  const videoUrl = value?.video_url || '';
  const posterUrl = value?.poster_url || '';

  // Show "Optimising video ✦" while the server transcodes. We can
  // detect that phase from the upload progress hitting 100 before
  // the response lands.
  useEffect(() => {
    if (pct >= 99 && busy) setStage('optimise');
  }, [pct, busy]);

  const onPick = () => {
    setError('');
    inputRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking same file
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      setError('Pick a video file');
      return;
    }
    if (file.size > 80 * 1024 * 1024) {
      setError('Clip is too big (max 80MB)');
      return;
    }
    setBusy(true); setStage('upload'); setPct(0);
    try {
      const form = new FormData();
      form.append('kind', kind);
      form.append('file', file);
      const r = await axios.post(`${API}/crew/upload/video`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (p) => {
          if (p.total) setPct(Math.min(99, Math.round((p.loaded / p.total) * 100)));
        },
        timeout: 180000,
      });
      setPct(100);
      onChange?.({
        video_url: r.data?.video_url || '',
        poster_url: r.data?.poster_url || '',
        duration_secs: r.data?.duration_secs || null,
      });
      setStage('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Upload failed');
      setStage('');
    } finally {
      setBusy(false);
      setTimeout(() => setPct(0), 600);
    }
  };

  const remove = () => {
    onChange?.({ video_url: '', poster_url: '', duration_secs: null });
  };

  return (
    <div data-testid={testId} style={{
      borderRadius: 16, padding: 16,
      background: `linear-gradient(135deg, ${PURPLE}1A 0%, ${PINK}10 100%)`,
      border: `1px solid ${PINK}44`,
      fontFamily: FONT_STACK, color: TEXT,
    }}>
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        capture={kind === 'profile' ? 'user' : undefined}
        onChange={onFile}
        style={{ display: 'none' }}
        data-testid={`${testId}-input`}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{
          width: 76, height: 76, borderRadius: 14, position: 'relative',
          background: posterUrl
            ? `url(${posterUrl}) center/cover`
            : `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {busy ? (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ProgressRing pct={pct} />
            </div>
          ) : videoUrl ? (
            <CheckCircle size={26} weight="fill" color="#fff" />
          ) : (
            <VideoCamera size={26} weight="fill" color="#fff" />
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontWeight: 800, fontSize: 14.5, color: TEXT }}>
            {videoUrl
              ? (kind === 'profile' ? 'Your 10-sec intro' : 'Vibe clip uploaded')
              : (kind === 'profile' ? 'Add a 10-sec intro' : 'Add a 6–20s vibe clip')}
          </p>
          <p style={{ margin: '3px 0 0', color: TEXT_MUTED, fontSize: 12, lineHeight: 1.45 }}>
            {busy
              ? (stage === 'optimise' ? 'Optimising video ✦' : 'Uploading…')
              : (videoUrl
                  ? 'Tap replace to swap or remove to clear.'
                  : `Vertical 9:16 plays best · ≤${maxSeconds}s · auto-compressed.`)}
          </p>
        </div>
      </div>

      {error && (
        <p data-testid={`${testId}-error`} style={{
          margin: '10px 0 0', color: PINK, fontSize: 12.5, fontWeight: 600,
        }}>
          {error}
        </p>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={onPick}
          disabled={busy}
          data-testid={`${testId}-pick`}
          style={{
            padding: '10px 16px', borderRadius: 999, border: 'none',
            cursor: busy ? 'wait' : 'pointer', color: TEXT,
            fontFamily: FONT_STACK, fontSize: 12.5, fontWeight: 800,
            background: `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
            boxShadow: `0 8px 20px ${PURPLE}44`,
            display: 'inline-flex', alignItems: 'center', gap: 6,
            opacity: busy ? 0.7 : 1,
          }}
        >
          <Lightning size={12} weight="fill" />
          {videoUrl ? 'Replace video' : 'Pick a video'}
        </button>
        {videoUrl && !busy && (
          <button
            type="button"
            onClick={remove}
            data-testid={`${testId}-remove`}
            style={{
              padding: '10px 14px', borderRadius: 999,
              background: 'transparent', border: `1px solid ${PANEL_BORDER}`,
              color: TEXT_MUTED, cursor: 'pointer',
              fontFamily: FONT_STACK, fontSize: 12, fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}
          >
            <Trash size={12} weight="bold" /> Remove
          </button>
        )}
      </div>
    </div>
  );
};

export default CrewVideoUploader;
